import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CartContext from '../../context/CartContext';
import {
  House,
  Package,
  Wallet,
  User,
  ShoppingCart,
  List,
  SignOut,
  CaretLeft,
  Bell,
  Tag
} from '@phosphor-icons/react';
import { Badge } from '../ui/badge';

const MobileNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isKycApproved } = useAuth();
  const { getCartCount } = useContext(CartContext);

  if (!isAuthenticated) return null;

  const navItems = [
    { icon: House, label: 'Home', path: '/' },
    { icon: Package, label: 'Products', path: '/products' },
    { icon: Tag, label: 'Offers', path: '/offers' },
    { icon: Wallet, label: 'Credit', path: '/wallets' },
    { icon: User, label: 'Profile', path: '/profile' }
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-50 md:hidden">
      <div className="flex justify-around items-center h-16 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center w-full h-full relative transition-colors ${
                isActive ? 'text-red-600' : 'text-neutral-500'
              }`}
            >
              <div className="relative">
                <Icon size={24} weight={isActive ? 'fill' : 'regular'} />
                {item.badge > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

const DesktopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, api } = useAuth();
  const { getCartCount } = useContext(CartContext);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (isAuthenticated && api) {
      api.get('/notifications/unread-count')
        .then(res => setUnreadCount(res.data.unread_count))
        .catch(() => {});
    }
  }, [isAuthenticated, location.pathname]);

  const navItems = [
    { label: 'Products', path: '/products' },
    { label: 'Offers', path: '/offers' },
    { label: 'Orders', path: '/orders' },
    { label: 'Statement', path: '/statement' },
    { label: 'Credit', path: '/wallets' }
  ];

  return (
    <header className="sticky top-0 bg-white border-b border-neutral-200 z-50 hidden md:block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="logo"
          >
            <span className="font-semibold text-lg font-outfit">Avantra Chemicals</span>
          </button>

          {isAuthenticated && (
            <>
              <nav className="flex items-center gap-8">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`text-sm font-medium transition-colors hover:text-red-600 ${
                      location.pathname === item.path ? 'text-red-600' : 'text-neutral-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/notifications')}
                  className="relative p-2 hover:bg-neutral-100 rounded-md transition-colors"
                  data-testid="bell-button"
                >
                  <Bell size={24} className="text-neutral-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => navigate('/cart')}
                  className="relative p-2 hover:bg-neutral-100 rounded-md transition-colors"
                  data-testid="cart-button"
                >
                  <ShoppingCart size={24} className="text-neutral-600" />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center gap-2 p-2 hover:bg-neutral-100 rounded-md transition-colors"
                  data-testid="profile-button"
                >
                  <User size={24} className="text-neutral-600" />
                  <span className="text-sm font-medium text-neutral-700">
                    {user?.full_name || 'Profile'}
                  </span>
                </button>

                <button
                  onClick={logout}
                  className="p-2 hover:bg-neutral-100 rounded-md transition-colors text-neutral-500"
                  data-testid="logout-button"
                >
                  <SignOut size={20} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const PageHeader = ({ title, showBack = false, onBack, rightContent }) => {
  const navigate = useNavigate();

  return (
    <div className="sticky top-0 bg-white border-b border-neutral-200 z-40 md:hidden">
      <div className="flex items-center justify-between h-14 px-4">
        <div className="flex items-center gap-3">
          {showBack && (
            <button
              onClick={onBack || (() => navigate(-1))}
              className="p-1 -ml-1"
              data-testid="back-button"
            >
              <CaretLeft size={24} className="text-neutral-700" />
            </button>
          )}
          <h1 className="text-lg font-semibold font-outfit text-neutral-900">{title}</h1>
        </div>
        {rightContent}
      </div>
    </div>
  );
};

export { MobileNav, DesktopNav, PageHeader };
